import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Fab from "@mui/material/Fab";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import CloseIcon from "@mui/icons-material/Close";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { getProblemById } from "../../models/problems";
import { leaveProblem } from "../../store/game/gameSlice";
import { COLOR_PRIMARY, COLOR_PRIMARY_DARK, COLOR_PRIMARY_LIGHT, COLOR_TEXT_MUTED } from "../../styles/colors";
import { trackEvent, GA_EVENTS } from "../../utils/analytics";
import { DifficultyChip, ProblemDetails } from "./ProblemDetails";

/** Brief for the active interview problem, docked on the canvas below the scoreboard. */
export const ProblemBriefPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const problem = useAppSelector((state) => getProblemById(state.game.activeProblemId));
  const [isOpen, setIsOpen] = useState(true);
  const isMobile = useMediaQuery(`(max-width: 550px)`);
  const showOneRow = useMediaQuery(`(min-width: 730px)`);

  // Re-open the brief whenever a different problem becomes active
  useEffect(() => {
    setIsOpen(true);
  }, [problem?.id]);

  if (!problem) return null;

  const position = {
    position: "absolute" as const,
    top: showOneRow ? 72 : 120,
    right: isMobile ? 10 : 16,
    zIndex: 99,
  };

  if (!isOpen) {
    // Sits directly above the zoom-to-fit button (48px, 24px from the bottom-right corner)
    return (
      <Tooltip title={`Show brief: ${problem.title}`} placement="left" arrow>
        <Fab
          aria-label="show problem brief"
          onClick={() => setIsOpen(true)}
          data-testid="problem-brief-fab"
          sx={{
            position: "absolute",
            bottom: 84,
            right: 24,
            zIndex: 10,
            width: 48,
            height: 48,
            color: "#fff",
            background: `linear-gradient(135deg, ${COLOR_PRIMARY_DARK} 0%, ${COLOR_PRIMARY_LIGHT} 100%)`,
            boxShadow: "0 4px 20px rgba(21, 118, 152, 0.55), 0 2px 8px rgba(0,0,0,0.2)",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
            "&:hover": {
              transform: "scale(1.12)",
              boxShadow: "0 6px 28px rgba(21, 118, 152, 0.75), 0 4px 12px rgba(0,0,0,0.25)",
            },
          }}
        >
          <AssignmentOutlinedIcon />
        </Fab>
      </Tooltip>
    );
  }

  return (
    <Card
      elevation={5}
      data-testid="problem-brief-panel"
      sx={{
        ...position,
        boxSizing: "border-box",
        ...(isMobile ? { left: 10 } : { width: 340 }),
        maxHeight: "calc(100% - 160px)",
        overflowY: "auto",
        overscrollBehavior: "contain",
        borderRadius: "12px",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="overline" sx={{ color: COLOR_TEXT_MUTED, lineHeight: 1.4 }}>
            Interview problem
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLOR_PRIMARY, lineHeight: 1.3 }}>
            {problem.title}
          </Typography>
        </Box>
        <DifficultyChip difficulty={problem.difficulty} />
        <Tooltip title="Hide brief" arrow>
          <IconButton size="small" aria-label="hide brief" onClick={() => setIsOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="body2" sx={{ color: COLOR_TEXT_MUTED }}>
        {problem.summary}
      </Typography>
      <ProblemDetails problem={problem} />
      <Button
        size="small"
        color="inherit"
        onClick={() => {
          trackEvent(GA_EVENTS.PROBLEM_LEFT, { problem_id: problem.id });
          dispatch(leaveProblem());
        }}
        data-testid="leave-problem-btn"
        sx={{ alignSelf: "flex-start", textTransform: "none", color: COLOR_TEXT_MUTED }}
      >
        Leave problem
      </Button>
    </Card>
  );
};
